'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { useQuery } from '@apollo/client/react';
import {
  DOCUMENT_CHAIN_QUERY,
  type RelatedDocument,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { Skeleton } from '@paeffgen/react/crm-documents/ui';
import {
  formatDate,
  TYPE_BADGES,
  TYPE_LABELS,
  TYPE_SHORT,
} from '@paeffgen/react/crm-documents/util';
import { clsx } from 'clsx';

interface DocumentChainProps {
  documentId: string;
}

export function DocumentChain({ documentId }: DocumentChainProps) {
  const { adminQueryVars } = useAdmin();

  const { data, loading } = useQuery<{
    documentChain: RelatedDocument[];
  }>(DOCUMENT_CHAIN_QUERY, {
    variables: { documentId, ...adminQueryVars },
    fetchPolicy: 'cache-first',
  });

  const chain = useMemo(() => {
    const list = data?.documentChain;
    if (!list || list.length === 0) return [];
    return [...list].sort((a, b) =>
      a.documentDate.localeCompare(b.documentDate)
    );
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center gap-4 pb-4 mb-6">
        <Skeleton className="h-16 w-[200px] rounded-lg" />
        <Skeleton className="h-16 w-[200px] rounded-lg" />
        <Skeleton className="h-16 w-[200px] rounded-lg" />
      </div>
    );
  }

  if (chain.length === 0) return null;

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6">
      {chain.map((item, index) => {
        const isCurrent = item.id === documentId;

        const cardClasses = clsx(
          'flex items-center gap-3 p-3 rounded-lg border transition-all shrink-0 min-w-[200px]',
          isCurrent
            ? 'bg-orange-50 border-primary shadow-sm ring-1 ring-primary'
            : 'bg-white border-gray-200 hover:border-primary hover:bg-orange-50/40 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
        );

        const cardContent = (
          <>
            <span
              className={clsx(
                'w-8 h-8 rounded-lg border flex items-center justify-center text-xs font-bold uppercase tracking-wide shrink-0',
                TYPE_BADGES[item.type]
              )}
            >
              {TYPE_SHORT[item.type]}
            </span>

            <div>
              <p
                className={clsx(
                  'text-xs font-semibold',
                  isCurrent ? 'text-primary' : 'text-zinc-900'
                )}
              >
                {TYPE_LABELS[item.type]}
              </p>
              <p className="text-xs text-gray-500 font-mono">
                {item.documentNumber}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {formatDate(item.documentDate)}
              </p>
            </div>
          </>
        );

        return (
          <React.Fragment key={item.id}>
            {index > 0 && (
              <ChevronRight
                className="text-gray-300 mx-2 shrink-0"
                size={16}
              />
            )}

            {isCurrent ? (
              <div className={cardClasses} aria-current="page">
                {cardContent}
              </div>
            ) : (
              <Link
                href={`/belege?open=${item.id}`}
                className={cardClasses}
                aria-label={`Beleg ${TYPE_LABELS[item.type]} ${item.documentNumber} öffnen`}
              >
                {cardContent}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
